import { useState, useEffect } from 'react';
import {
  fetchAdminContent,
  saveAdminContent,
  deleteAdminContent,
  type AdminItem,
} from '../../admin/contentApi.js';
import { ContentOrigin } from '../../admin/ContentOrigin.js';
import { CONDITION_OPTIONS } from '../../admin/vocab.js';
import { itemDestiny } from '../../game/items.js';

export type ItemCategory =
  | 'cura_hp'
  | 'cura_pm'
  | 'revive'
  | 'status'
  | 'boost_stat'
  | 'utilitario';

export interface ItemEffect {
  readonly code: number;
  readonly dataId: number;
  readonly value1: number;
  readonly value2: number;
}

export interface ItemDef {
  readonly id: number;
  readonly name: string;
  readonly consumable?: boolean;
  readonly scope?: number;
  readonly uses?: number;
  readonly price?: number;
  readonly icon?: number;
  readonly animationId?: number;
  /** Os efeitos do RPG Maker, como a origem os trouxe. Registro, não regra. */
  readonly effects?: readonly ItemEffect[];
  readonly description?: string;
  readonly category?: ItemCategory;
  /*
   * O que o jogo lê de fato — traduzido pelo pipeline e editável aqui. O motor
   * não interpreta os códigos do RPG Maker: ele lê estes campos (ver ASSETS.md
   * e `Oz\Content\ItemUse`).
   */
  readonly heal?: number;
  readonly healRate?: number;
  readonly cures?: readonly string[];
  readonly opensChests?: boolean;
  readonly boost?: { readonly stat: string; readonly amount: number } | undefined;
}

const CATEGORIES: { id: ItemCategory | 'todas'; label: string; icon: string }[] = [
  { id: 'todas', label: 'Todos', icon: '📦' },
  { id: 'cura_hp', label: 'Cura HP', icon: '🧪' },
  { id: 'cura_pm', label: 'Mana PM', icon: '💧' },
  { id: 'revive', label: 'Reanimação', icon: '✨' },
  { id: 'status', label: 'Remédios', icon: '🌿' },
  { id: 'boost_stat', label: 'Atributos', icon: '⭐' },
  { id: 'utilitario', label: 'Utilitários', icon: '🗝️' },
];

function detectCategory(item: ItemDef): ItemCategory {
  if (item.category) return item.category;

  // Primeiro o que o jogo lê; os códigos da origem são a reserva, para o item
  // cujo efeito não tem tradução no tabuleiro (PM, reviver).
  if ((item.heal ?? 0) > 0 || (item.healRate ?? 0) > 0) return 'cura_hp';
  if ((item.cures?.length ?? 0) > 0) return 'status';
  if (item.boost) return 'boost_stat';
  if (item.opensChests) return 'utilitario';

  const effects = item.effects ?? [];
  for (const eff of effects) {
    if (eff.code === 11) return 'cura_hp';
    if (eff.code === 12) return 'cura_pm';
    if (eff.code === 22 && eff.dataId === 1) return 'revive';
    if (eff.code === 22) return 'status';
    if (eff.code === 42) return 'boost_stat';
  }
  const name = item.name.toLowerCase();
  if (name.includes('poção') || name.includes('vulnerary') || name.includes('pv')) return 'cura_hp';
  if (name.includes('água mágica') || name.includes('pm')) return 'cura_pm';
  if (name.includes('estimulante') || name.includes('reviv')) return 'revive';
  if (name.includes('antídoto') || name.includes('erva')) return 'status';
  if (name.includes('aumento') || name.includes('túnica') || name.includes('gota') || name.includes('asa') || name.includes('livro') || name.includes('escudo') || name.includes('talismã')) return 'boost_stat';
  return 'utilitario';
}

/** Os oito atributos do motor — os mesmos que a ficha mostra. */
const STAT_NAMES: Record<string, string> = {
  hp: 'PV Máximo',
  str: 'Força',
  mag: 'Magia',
  skl: 'Destreza',
  spd: 'Velocidade',
  lck: 'Sorte',
  def: 'Defesa',
  res: 'Resistência',
};

const DESTINY_LABEL: Record<string, string> = {
  batalha: '🎒 Vai para a bolsa e se usa em campo',
  ficha: '⬆ Reforço permanente, usado na ficha (aba Comunidade)',
  nenhum: '— Sem efeito no jogo: fica no inventário da conta, e a loja não o vende',
};

export function ItemEditor(): JSX.Element {
  const [items, setItems] = useState<AdminItem<ItemDef>[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filterCategory, setFilterCategory] = useState<ItemCategory | 'todas'>('todas');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Estados do formulário
  const [name, setName] = useState('');
  const [category, setCategory] = useState<ItemCategory>('cura_hp');
  const [consumable, setConsumable] = useState(true);
  const [scope, setScope] = useState(7); // 7: self, 1: ally, 9: dead ally, 0: map
  const [uses, setUses] = useState(3);
  const [price, setPrice] = useState(300);
  const [icon, setIcon] = useState(176);
  const [animationId, setAnimationId] = useState(41);
  const [description, setDescription] = useState('');

  /*
   * O que o jogo lê. Não são mais os códigos do RPG Maker: o motor lê `heal`,
   * `healRate`, `cures`, `opensChests` e `boost`, e é isto que o formulário
   * edita. Os `effects` da origem ficam guardados no registro, intocados.
   */
  const [hpFixed, setHpFixed] = useState(10);
  const [hpPercent, setHpPercent] = useState(0);
  const [cures, setCures] = useState<string[]>([]);
  const [opensChests, setOpensChests] = useState(false);
  const [boostStat, setBoostStat] = useState('hp');
  const [boostAmount, setBoostAmount] = useState(2);

  const [isNew, setIsNew] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminContent<ItemDef>('items');
      setItems(data);
      if (data.length > 0 && selectedId === null && data[0]) {
        selectItem(data[0]);
      }
    } catch (e) {
      setMessage({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const selectItem = (it: AdminItem<ItemDef>) => {
    setSelectedId(it.contentId);
    setIsNew(false);
    setName(it.name || it.data.name || '');
    const cat = detectCategory(it.data);
    setCategory(cat);
    setConsumable(it.data.consumable !== false);
    setScope(it.data.scope ?? 7);
    setUses(it.data.uses ?? 1);
    setPrice(it.data.price ?? 100);
    setIcon(it.data.icon ?? 176);
    setAnimationId(it.data.animationId ?? 41);
    setDescription(it.data.description ?? '');

    setHpFixed(it.data.heal ?? 0);
    setHpPercent(Math.round((it.data.healRate ?? 0) * 100));
    setCures([...(it.data.cures ?? [])]);
    setOpensChests(Boolean(it.data.opensChests));
    setBoostStat(it.data.boost?.stat ?? 'hp');
    setBoostAmount(it.data.boost?.amount ?? 2);
    setMessage(null);
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsNew(true);
    setName('Novo Item Consumível');
    setCategory('cura_hp');
    setConsumable(true);
    setScope(7);
    setUses(3);
    setPrice(300);
    setIcon(176);
    setAnimationId(41);
    setDescription('Recupera pontos de vida no campo de batalha.');
    setHpFixed(15);
    setHpPercent(0);
    setCures([]);
    setOpensChests(false);
    setBoostStat('hp');
    setBoostAmount(2);
    setMessage(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ type: 'err', text: 'O nome do item é obrigatório.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    /*
     * O que o item faz, na régua do tabuleiro.
     *
     * Antes este formulário remontava os `effects` do RPG Maker — e o motor
     * não os lê. Pior: os valores da origem estão na escala do RPG (a Poção
     * "cura 500"), e regravá-los como se fossem PV do tático teria feito uma
     * poção curar dez vezes o esquadrão. Agora o que se edita é o que o jogo
     * lê, e os `effects` da origem ficam intactos no registro.
     */
    const battle: Partial<ItemDef> = {
      heal: category === 'cura_hp' ? Math.max(0, Number(hpFixed)) : 0,
      healRate: category === 'cura_hp' ? Math.max(0, Math.min(100, Number(hpPercent))) / 100 : 0,
      cures: category === 'status' ? cures : [],
      opensChests: category === 'utilitario' ? opensChests : false,
      ...(category === 'boost_stat'
        ? { boost: { stat: boostStat, amount: Math.max(1, Number(boostAmount)) } }
        : { boost: undefined }),
    };

    /*
     * O que o formulário não mostra tem de sobreviver ao salvamento.
     *
     * A gravação substitui o registro inteiro pelo que sobe daqui, e há campos
     * que este formulário não edita — de qual jogo importado o registro veio,
     * de qual folha o ícone fala, e o que o conversor trouxe da origem.
     * Montando o pacote em cima da ficha atual, um ajuste de preço deixa de
     * apagá-los.
     */
    const existing = items.find((it) => it.contentId === selectedId)?.data;

    const payload: Partial<ItemDef> & { name: string } = {
      ...(isNew || !existing ? {} : existing),
      name: name.trim(),
      category,
      consumable: Boolean(consumable),
      scope: Number(scope),
      uses: Number(uses),
      price: Number(price),
      icon: Number(icon),
      animationId: Number(animationId),
      ...battle,
      description,
    };

    const targetId = isNew ? null : selectedId;
    const res = await saveAdminContent<ItemDef>('items', targetId, payload);

    setSaving(false);
    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao salvar item.' });
    } else {
      setMessage({ type: 'ok', text: `Item "${name}" salvo com sucesso!` });
      await loadData();
      if (res.item) {
        selectItem(res.item);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Tem certeza que deseja excluir o item #${selectedId} (${name})?`)) {
      return;
    }

    setSaving(true);
    const res = await deleteAdminContent('items', selectedId);
    setSaving(false);

    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao excluir item.' });
    } else {
      setMessage({ type: 'ok', text: `Item #${selectedId} excluído com sucesso.` });
      const remaining = items.filter((it) => it.contentId !== selectedId);
      setItems(remaining);
      if (remaining.length > 0 && remaining[0]) {
        selectItem(remaining[0]);
      } else {
        handleNew();
      }
    }
  };

  const handleDuplicate = () => {
    setSelectedId(null);
    setIsNew(true);
    setName(`${name} (Cópia)`);
    setMessage({ type: 'ok', text: 'Cópia pronta para ajustes. Clique em Salvar para gravar no banco.' });
  };

  const filteredItems = items.filter((it) => {
    const cat = detectCategory(it.data);
    const matchesCategory = filterCategory === 'todas' || cat === filterCategory;
    const matchesText =
      it.name.toLowerCase().includes(filterText.toLowerCase()) ||
      String(it.contentId).includes(filterText) ||
      (it.data.description && it.data.description.toLowerCase().includes(filterText.toLowerCase()));
    return matchesCategory && matchesText;
  });

  const currentItem = items.find((it) => it.contentId === selectedId);

  // Para onde este item vai, pela mesma regra do servidor e da aba Comunidade.
  const destiny = itemDestiny({
    id: selectedId ?? 0,
    name,
    ...(category === 'cura_hp' ? { heal: hpFixed, healRate: hpPercent / 100 } : {}),
    ...(category === 'status' ? { cures } : {}),
    ...(category === 'utilitario' && opensChests ? { opensChests: true } : {}),
    ...(category === 'boost_stat' ? { boost: { stat: boostStat, amount: boostAmount } } : {}),
  });

  return (
    <div className="admin-db-container">
      {/* Coluna Esquerda: Filtros e Lista */}
      <aside className="admin-sidebar-list">
        <div className="admin-list-header">
          <input
            type="text"
            className="admin-search-input"
            placeholder="🔍 Filtrar poções, elixires, tônicos..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />

          <div className="admin-filter-pill-row">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`filter-pill ${filterCategory === c.id ? 'active' : ''}`}
                onClick={() => setFilterCategory(c.id)}
                title={c.label}
              >
                {c.icon} {c.label}
              </button>
            ))}
          </div>

          <button type="button" className="admin-btn-primary admin-btn-new" onClick={handleNew}>
            + Novo Item
          </button>
        </div>

        {loading ? (
          <div className="admin-loading-indicator">Carregando itens do acervo…</div>
        ) : (
          <div className="admin-items-scroll">
            {filteredItems.map((it) => {
              const active = !isNew && it.contentId === selectedId;
              const cat = detectCategory(it.data);
              const catDef = CATEGORIES.find((c) => c.id === cat);

              // O resumo sai do que o JOGO lê, e não dos códigos da origem: a
              // Poção cura 23 no tabuleiro, ainda que o RPG diga 500.
              let summary = '';
              const { heal = 0, healRate = 0 } = it.data;
              const boost = it.data.boost;
              if (healRate > 0) summary = `${Math.round(healRate * 100)}% PV`;
              else if (heal > 0) summary = `${heal} PV`;
              else if (boost) summary = `+${boost.amount} ${STAT_NAMES[boost.stat] ?? boost.stat}`;
              else if ((it.data.cures?.length ?? 0) > 0) summary = 'Cura condição';
              else if (it.data.opensChests) summary = 'Abre baú';
              else summary = 'sem efeito';

              return (
                <button
                  key={it.contentId}
                  type="button"
                  className={`admin-list-item-btn ${active ? 'active' : ''}`}
                  onClick={() => selectItem(it)}
                >
                  <span className="admin-item-id">
                    {String(it.contentId).padStart(3, '0')}:
                  </span>
                  <span className="admin-item-weapon-icon">{catDef?.icon ?? '🧪'}</span>
                  <span className="admin-item-name">{it.name}</span>
                  {it.isCustom && <span className="admin-badge-custom">Custom</span>}
                  <span className="admin-item-meta">
                    {summary ? `${summary} · ` : ''}{it.data.uses ?? 1} uso{(it.data.uses ?? 1) > 1 ? 's' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      {/* Coluna Direita: Formulário */}
      <main className="admin-main-form">
        <div className="admin-form-top-bar">
          <div className="admin-form-title-area">
            <h2>
              {isNew ? '✨ Criando Novo Item' : `🧪 Item #${selectedId}: ${name}`}
            </h2>
            {currentItem && (
              <ContentOrigin item={currentItem} />
            )}
          </div>

          <div className="admin-form-actions">
            {!isNew && (
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={handleDuplicate}
                title="Duplicar como novo item"
              >
                📋 Duplicar
              </button>
            )}
            {!isNew && (
              <button
                type="button"
                className="admin-btn-danger"
                onClick={handleDelete}
                disabled={saving}
                title="Excluir este item"
              >
                🗑️ Excluir
              </button>
            )}
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Gravando…' : '💾 Salvar no Banco'}
            </button>
          </div>
        </div>

        {message && (
          <div className={`admin-alert ${message.type === 'ok' ? 'alert-success' : 'alert-error'}`}>
            {message.text}
          </div>
        )}

        {/* Para onde cada item vai depende do efeito dele — ver `ItemUse`. */}
        <p className="admin-helper-text">
          Itens entram na loja e nas recompensas de capítulo, vão para o inventário da conta e de lá
          para a <strong>bolsa</strong> de um personagem (se curam PV, curam condição ou abrem baú)
          ou para a <strong>ficha</strong> dele (se são reforço permanente). O que não tem nenhum dos
          dois efeitos fica parado, e a loja não o vende.
        </p>

        <div className="admin-form-sections-grid">
          {/* Cartão 1: Identificação & Classificação */}
          <section className="admin-form-card">
            <h3 className="admin-card-title">📦 Identificação & Categoria</h3>

            <div className="admin-form-field">
              <label htmlFor="item-name">Nome do Item:</label>
              <input
                id="item-name"
                type="text"
                className="admin-input-text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Poção Tática, Elixir de Convalescença..."
              />
            </div>

            <div className="admin-form-row">
              <div className="admin-form-field">
                <label htmlFor="item-cat">Categoria Funcional:</label>
                <select
                  id="item-cat"
                  className="admin-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ItemCategory)}
                >
                  <option value="cura_hp">🧪 Cura de Pontos de Vida (PV)</option>
                  <option value="cura_pm">💧 Cura de Pontos de Magia (PM)</option>
                  <option value="revive">✨ Reanimação de Combatente Morto</option>
                  <option value="status">🌿 Remédio / Cura de Condições</option>
                  <option value="boost_stat">⭐ Fortalecedor Permanente (Tônico / Robe)</option>
                  <option value="utilitario">🗝️ Utilitário / Chave / Promoção</option>
                </select>
              </div>

              <div className="admin-form-field">
                <label htmlFor="item-scope">Escopo / Alvo de Uso:</label>
                <select
                  id="item-scope"
                  className="admin-select"
                  value={scope}
                  onChange={(e) => setScope(Number(e.target.value))}
                >
                  <option value={7}>👤 Próprio Combatente (Self)</option>
                  <option value={1}>🤝 Aliado Adjacente (Alcance 1)</option>
                  <option value={9}>💀 Aliado Nocauteado (Revive)</option>
                  <option value={11}>🌐 Todos os Aliados (Grupo)</option>
                  <option value={0}>🗺️ Mapa / Especial (Chaves/Portas)</option>
                </select>
              </div>
            </div>

            <div className="admin-form-row">
              <div className="admin-form-field">
                <label htmlFor="item-uses">Quantidade de Usos:</label>
                <input
                  id="item-uses"
                  type="number"
                  min={1}
                  max={99}
                  className="admin-input-number"
                  value={uses}
                  onChange={(e) => setUses(Number(e.target.value) || 1)}
                />
              </div>

              <div className="admin-form-field">
                <label htmlFor="item-price">Preço de Compra (Ouro):</label>
                <input
                  id="item-price"
                  type="number"
                  min={0}
                  className="admin-input-number"
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value) || 0)}
                />
              </div>

              <div className="admin-form-field">
                <label htmlFor="item-icon">Ícone MZ / Sprite:</label>
                <input
                  id="item-icon"
                  type="number"
                  min={0}
                  className="admin-input-number"
                  value={icon}
                  onChange={(e) => setIcon(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="admin-form-field checkbox-line">
              <label>
                <input
                  type="checkbox"
                  checked={consumable}
                  onChange={(e) => setConsumable(e.target.checked)}
                />
                <span>Consumível (desaparece do inventário ao esgotar os usos)</span>
              </label>
            </div>
          </section>

          {/* Cartão 2: Efeitos e Parâmetros Específicos */}
          <section className="admin-form-card">
            <h3 className="admin-card-title">⚡ Efeitos e Fórmulas de Cura/Bônus</h3>

            <p className="admin-field-tip">
              <strong>{DESTINY_LABEL[destiny]}</strong>
            </p>

            {category === 'cura_hp' && (
              <div className="admin-form-group">
                <p className="admin-field-tip">
                  Na régua do tabuleiro (PV de 14 a 60), e não na do RPG: uma poção que
                  cura 10 repõe quase metade de um recruta. Os dois valores somam.
                </p>
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label htmlFor="hp-fixed">Cura Fixa de PV:</label>
                    <input
                      id="hp-fixed"
                      type="number"
                      min={0}
                      className="admin-input-number"
                      value={hpFixed}
                      onChange={(e) => setHpFixed(Number(e.target.value) || 0)}
                      placeholder="Ex: 10 ou 500"
                    />
                  </div>
                  <div className="admin-form-field">
                    <label htmlFor="hp-pct">Cura Percentual (% PV Máx):</label>
                    <input
                      id="hp-pct"
                      type="number"
                      min={0}
                      max={100}
                      className="admin-input-number"
                      value={hpPercent}
                      onChange={(e) => setHpPercent(Number(e.target.value) || 0)}
                      placeholder="Ex: 50 ou 100"
                    />
                  </div>
                </div>
              </div>
            )}

            {category === 'cura_pm' && (
              <div className="admin-form-group">
                <p className="admin-field-tip">
                  <strong>Sem efeito no tabuleiro.</strong> Aqui a magia não gasta pontos: gasta
                  usos do cajado. Para dar utilidade a este item, escolha outra categoria —
                  cura de PV, remédio ou reforço de atributo.
                </p>
              </div>
            )}

            {category === 'revive' && (
              <div className="admin-form-group">
                <p className="admin-field-tip">
                  <strong>Sem efeito no tabuleiro.</strong> O motor não traz ninguém de volta
                  em campo; quem cai, cai. O item continua no acervo e no inventário da conta.
                </p>
              </div>
            )}

            {category === 'status' && (
              <div className="admin-form-group">
                <p className="admin-field-tip">
                  As condições que este remédio tira de quem o usa — nele mesmo ou num aliado
                  encostado. São as seis que o motor conhece.
                </p>
                <div className="admin-checkbox-grid">
                  {CONDITION_OPTIONS.map((c) => (
                    <label key={c.id} className="checkbox-line" title={c.summary}>
                      <input
                        type="checkbox"
                        checked={cures.includes(c.id)}
                        onChange={(e) =>
                          setCures((current) =>
                            e.target.checked
                              ? [...current, c.id]
                              : current.filter((id) => id !== c.id),
                          )
                        }
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {category === 'boost_stat' && (
              <div className="admin-form-group">
                <p className="admin-field-tip">
                  Item de incremento definitivo (estilo Túnica de Anjo, Gota de Energia, Asa Ligeira):
                </p>
                <div className="admin-form-row">
                  <div className="admin-form-field">
                    <label htmlFor="stat-target">Atributo Alvo:</label>
                    <select
                      id="stat-target"
                      className="admin-select"
                      value={boostStat}
                      onChange={(e) => setBoostStat(e.target.value)}
                    >
                      {Object.entries(STAT_NAMES).map(([key, sName]) => (
                        <option key={key} value={key}>
                          {sName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-form-field">
                    <label htmlFor="stat-amt">Aumento Permanente (+):</label>
                    <input
                      id="stat-amt"
                      type="number"
                      min={1}
                      max={20}
                      className="admin-input-number"
                      value={boostAmount}
                      onChange={(e) => setBoostAmount(Number(e.target.value) || 1)}
                    />
                  </div>
                </div>
                <p className="admin-field-tip">
                  Usa-se fora da partida, na ficha do personagem (aba Comunidade), e o servidor
                  aplica: em campo o motor não tem reforço permanente, e a conferência do
                  esquadrão recusaria o ponto como trapaça.
                </p>
              </div>
            )}

            {category === 'utilitario' && (
              <div className="admin-form-group">
                <p className="admin-field-tip">
                  Item de função tática específica. A única que o motor conhece é a chave:
                  porta trancada e promoção de classe ainda não existem no tabuleiro.
                </p>
                <div className="admin-form-field checkbox-line">
                  <label>
                    <input
                      type="checkbox"
                      checked={opensChests}
                      onChange={(e) => setOpensChests(e.target.checked)}
                    />
                    <span>Abre baú trancado (some ao usar)</span>
                  </label>
                </div>
              </div>
            )}

            <div className="admin-form-field" style={{ marginTop: '1rem' }}>
              <label htmlFor="item-anim">ID da Animação MZ de Efeito:</label>
              <input
                id="item-anim"
                type="number"
                min={0}
                className="admin-input-number"
                value={animationId}
                onChange={(e) => setAnimationId(Number(e.target.value) || 0)}
              />
            </div>
          </section>

          {/* Cartão 3: Descrição e Lore */}
          <section className="admin-form-card" style={{ gridColumn: 'span 2' }}>
            <h3 className="admin-card-title">📝 Descrição em Jogo</h3>
            <div className="admin-form-field">
              <textarea
                className="admin-textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Explique o que este item faz para os combatentes em campo..."
              />
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
